import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown, GalleryVerticalEnd, Globe, LogOut } from 'lucide-react'
import { ErrorBoundary } from 'react-error-boundary'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { Button } from 'shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Separator } from 'shared/components/ui/separator'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarRail,
	SidebarTrigger,
} from 'shared/components/ui/sidebar'
import { useSidebar } from 'shared/components/ui/sidebar-context'
import { Flag } from 'shared/components/flag'
import { LanguageRadioItems } from 'shared/components/language-choice'
import { useLanguage } from 'shared/components/use-language'
import { LANGUAGE_NAMES, useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { useAuthStore } from 'shared/store/auth'
import { ErrorFallbackMain } from '../error-boundaries/ErrorFallback'
import { trpc } from 'shared/api'
import type { Role } from 'shared/lib/roles'
import { isCurrent, sidebarFor, sidebarGroups, watchesInventory } from '../routes'
import { NavLabel } from './NavLabel'
import { RefreshBtn } from './refreshBtn'
import { AppInitializers } from './AppInitializers'

/**
 * The sections the sidebar offers a role now: the operator's inventory screen only
 * while their Point has an open inventory (ticket 54) — asked of the server, and
 * re-asked when any change refetches the active queries.
 */
function useOfferedSections(role: Role) {
	const watching = watchesInventory(role)
	const open = useQuery({ ...trpc.inventories.AtMyPoint.queryOptions(), enabled: watching })
	return sidebarFor(role, watching && (open.data?.length ?? 0) > 0)
}

/**
 * The signed-in person at the foot of the sidebar: who they are, opening a menu with
 * the panel's language and the way out.
 */
function NavUser() {
	const navigate = useNavigate()
	const { t } = useTranslate()
	const { isMobile } = useSidebar()
	const { user, logout } = useAuthStore()
	const { lang } = useLanguage()

	const handleLogout = () => {
		logout()
		navigate('/login', { replace: true })
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size='lg'
							className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
							data-testid='user-menu'
						>
							<div className='flex flex-1 flex-col gap-0.5 leading-none'>
								<span className='text-sm font-medium'>{user.username}</span>
								<span className='text-xs text-muted-foreground'>{t(`roles.${user.role}`)}</span>
							</div>
							<ChevronsUpDown className='ml-auto size-4' />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
						side={isMobile ? 'bottom' : 'right'}
						align='end'
						sideOffset={4}
					>
						<DropdownMenuSub>
							<DropdownMenuSubTrigger data-testid='user-menu-language'>
								<Globe />
								{t('settings.language')}
								<span className='ml-auto flex items-center gap-1.5 text-sm font-medium'>
									<Flag lang={lang} className='size-auto h-3.5 rounded-xs' />
									{LANGUAGE_NAMES[lang]}
								</span>
							</DropdownMenuSubTrigger>
							<DropdownMenuSubContent>
								<LanguageRadioItems />
							</DropdownMenuSubContent>
						</DropdownMenuSub>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={handleLogout} data-testid='logout'>
							<LogOut />
							{t('nav.logout')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}

export function AppSidebar() {
	const location = useLocation()
	const { t } = useTranslate()
	const { user } = useAuthStore()
	const sections = useOfferedSections(user.role)

	return (
		<ErrorBoundary fallback={'Oops, an error occurred!'}>
			<Sidebar>
				<SidebarHeader>
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton size='lg' asChild>
								<Link to={sections[0]?.path ?? '/'}>
									<div className='bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg'>
										<GalleryVerticalEnd className='size-4' />
									</div>
									<div className='flex flex-col gap-0.5 leading-none'>
										<span className='font-semibold'>TezGo</span>
										<span className='text-xs text-muted-foreground'>{t('nav.panel')}</span>
									</div>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarHeader>
				<SidebarContent className='gap-0'>
					{sidebarGroups(sections).map((block) => (
						<SidebarGroup key={block.group ?? ''} data-testid={block.group && `sidebar-group-${block.group.replace(/^nav\./, '')}`}>
							{block.group && (
								<SidebarGroupLabel>
									<NavLabel label={block.group} />
								</SidebarGroupLabel>
							)}
							<SidebarGroupContent>
								<SidebarMenu>
									{block.sections.map((item) => {
										const current = isCurrent(item, location.pathname)
										return (
											<SidebarMenuItem key={item.path}>
												<SidebarMenuButton
													asChild
													isActive={current}
													className='data-active:bg-sidebar-foreground/10 data-active:font-semibold data-active:text-sidebar-foreground data-active:hover:bg-sidebar-foreground/10'
												>
													<Link
														to={item.path}
														data-testid={`nav-${item.path.slice(1)}`}
														aria-current={current ? 'page' : undefined}
													>
														{item.icon}
														<NavLabel label={item.label} />
													</Link>
												</SidebarMenuButton>
											</SidebarMenuItem>
										)
									})}
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					))}
				</SidebarContent>
				<SidebarFooter>
					<NavUser />
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>
		</ErrorBoundary>
	)
}

export function AppLayout() {
	return (
		<SidebarProvider>
			<AppSidebar />
			<SidebarInset>
				<header className='bg-background sticky top-0 flex h-14 shrink-0 items-center gap-2 border-b px-4'>
					<SidebarTrigger className='-ml-2' />
					<Separator orientation='vertical' className='mr-2 h-full' />
					<RefreshBtn />
				</header>
				<main className='flex-1 p-4'>
					<ErrorBoundary FallbackComponent={ErrorFallbackMain}>
						<Outlet />
					</ErrorBoundary>
				</main>

				<AppInitializers />
			</SidebarInset>
		</SidebarProvider>
	)
}

/**
 * The layout of a role that works from a phone (`PHONE_ROLES` — the driver): one
 * column sized for a phone, its sections as tabs across the top, no sidebar. It is
 * the same application and the same internal surface (ADR-0029), only laid out
 * for the hand.
 */
export function PhoneLayout() {
	const { t } = useTranslate()
	const navigate = useNavigate()
	const { user, logout } = useAuthStore()
	const sections = useOfferedSections(user.role)

	const handleLogout = () => {
		logout()
		navigate('/login', { replace: true })
	}

	return (
		<div className='flex min-h-svh flex-col' data-testid='phone-layout'>
			<header className='bg-background sticky top-0 z-10 border-b'>
				<div className='mx-auto flex h-12 max-w-md items-center gap-2 px-3'>
					<span className='font-semibold'>TezGo</span>
					<span className='flex-1 truncate text-right text-xs text-muted-foreground'>
						{user.name} · {t(`roles.${user.role}`)}
					</span>
					<Button
						variant='ghost'
						size='icon-sm'
						onClick={handleLogout}
						title='Logout'
						data-testid='logout'
					>
						<LogOut className='size-4' />
					</Button>
				</div>
				<nav className='mx-auto flex max-w-md gap-1 overflow-x-auto px-3 pb-2'>
					{sections.map((item) => (
						<NavLink
							key={item.path}
							to={item.path}
							data-testid={`nav-${item.path.slice(1)}`}
							className={({ isActive }) =>
								cn(
									'flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm [&_svg]:size-4',
									isActive
										? 'bg-primary text-primary-foreground'
										: 'text-muted-foreground',
								)
							}
						>
							{item.icon}
							<NavLabel label={item.label} />
						</NavLink>
					))}
				</nav>
			</header>
			<main className='mx-auto w-full max-w-md flex-1 p-3'>
				<ErrorBoundary FallbackComponent={ErrorFallbackMain}>
					<Outlet />
				</ErrorBoundary>
			</main>
			<AppInitializers />
		</div>
	)
}
